/* TODO: t() */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { postJSON } from '../../support/Fetch';
import { isValidEmail } from '../../core/support/isValidEmail';
import {
  DEFAULT_USER_PERMISSIONS,
  AccessLevel,
  AccountPermissionKeys,
} from '../../support/shared/constants/UserConstants';
import isPasswordStrengthEnough from '../../support/shared/isPasswordStrengthEnough';
import UserActions from '../../support/UserActions';
import { Account } from '../../types/shared/Account';
import { User } from '../../types/shared/User';
import { showError, showSuccess } from '../App/AppAlert';
import { showDialog } from '../App/AppDialog';
import LoadingIndicator from '../uiold/LoadingIndicator';
import {
  BooleanInput,
  Button,
  ButtonToolbar,
  DummyLogin,
  Form,
  FormField,
  Text,
  TextInputPassword,
  TextInputWithError,
} from '../uiold';
import { themeColors } from '../uiold/Constants';
import { PermissionSelectRadio } from './PermissionSelectRadio';

type Props = {
  account: Account;
  user: User | null | undefined;
  onClose: () => void;
  selectedID: string | null | undefined;
};

const VALID_USERNAME = /^[a-zA-Z][a-zA-Z0-9@._-]+[a-zA-Z0-9]$/;

// A temporary placeholder ID for new (not yet saved) user.
const NEW_USER_ID = '-1';

function UserDetailsContainer(props: Props) {
  let { user, selectedID, onClose, account } = props;
  let isSessionSystemAdmin = account.system_admin;
  if (selectedID === NEW_USER_ID) {
    return (
      <UserDetails
        key={selectedID}
        user={null}
        onClose={onClose}
        account={account}
      />
    );
  }
  if (!user) {
    return (
      <View style={styles.emptyState}>
        <Text style={styles.emptyStateText}>
          {isSessionSystemAdmin
            ? t('Select a user from the list')
            : t('Select a user from the list, or press Add User')}
        </Text>
      </View>
    );
  }
  return (
    <UserDetails
      key={selectedID}
      user={user}
      onClose={onClose}
      account={account}
    />
  );
}

type FormData = typeof initialFormState;

let initialFormState = {
  name: '',
  email: '',
  username: '',
  password: '',
  suspended: false,
  permissions: DEFAULT_USER_PERMISSIONS,
};

function UserDetails(props: {
  account: Account;
  user: User | null;
  onClose: () => void;
}) {
  let { user, onClose, account } = props;
  let isSessionSystemAdmin = account.system_admin;
  let [formData, setFormData] = useState<FormData>({
    name: user?.name ?? initialFormState.name,
    email: user?.email ?? initialFormState.email,
    username: user?.username ?? initialFormState.username,
    password: initialFormState.password,
    suspended: user?.suspended ?? initialFormState.suspended,
    permissions: user?.permissions ?? initialFormState.permissions,
  });
  let [isLoading, setIsLoading] = useState(false);
  let { password } = formData;

  let onSave = async () => {
    let data = {
      ...formData,
      account_id: account.id,
    };
    let checkData = checkUserFormData(data, user);
    if (checkData?.errorMessage) {
      showError(checkData.errorMessage);
      return;
    }
    if (user == null) {
      // New User
      setIsLoading(true);
      let url = '/api/users';
      let result = await postJSON(url, { user: data });
      setIsLoading(false);
      if (result?.success === true) {
        UserActions.addUser(result.user);
        showSuccess(result.successMessage);
        onClose();
      } else {
        showError(result?.errorMessage);
      }
    } else {
      // Get current password if nothing is typed
      password = password.length === 0 ? user.password : password;
      data.password = password;
      // Update User
      let userUpdates = { id: user?.id, ...data };
      if (isSessionSystemAdmin) {
        userUpdates = { ...userUpdates };
      }
      setIsLoading(true);
      let url = `/api/users/${user?.id}`;
      let result = await postJSON(url, { user: userUpdates });
      setIsLoading(false);
      if (result?.success === true) {
        UserActions.updateUserById(user.id, userUpdates);
        showSuccess(result.successMessage);
        onClose();
      } else {
        showError(result?.errorMessage);
      }
    }
  };

  let onChange = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData({ ...formData, [key]: value });
  };

  let onChangePermissions = (
    permissionKey: AccountPermissionKeys,
    value: AccessLevel,
  ) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [permissionKey]: value,
      },
    });
  };

  let onDeletePress = () => {
    const CANCEL = t('Cancel');
    const DELETE = t('Delete');
    showDialog(
      t('Delete User?'),
      t('Are you sure you want to permanently delete this user?'),
      [CANCEL, DELETE],
      (value) => {
        if (value === CANCEL) {
          return;
        }
        deleteUser();
        onClose();
      },
    );
  };

  let deleteUser = async () => {
    if (!user) {
      return;
    }
    setIsLoading(true);
    let url = `/api/users/${user.id}/delete`;
    let result = await postJSON(url, {});
    if (result.success === true) {
      UserActions.deleteUserById(user.id);
    }
    setIsLoading(false);
  };

  let userPermissions =
    formData?.permissions && Object.keys(formData.permissions);

  if (isLoading) {
    return (
      <View style={styles.root}>
        <LoadingIndicator />
      </View>
    );
  }
  return (
    <View style={styles.root}>
      <View style={styles.header}>
        {user ? (
          <>
            <Text style={styles.headingTwo}>{t('Edit User')}</Text>
          </>
        ) : (
          <View style={styles.headerNewAccount}>
            <Text style={styles.headingTwo}>{t('Add New User')}</Text>
          </View>
        )}
        <ButtonToolbar style={styles.toolbar}>
          {user && (
            <Button
              title={t('Delete')}
              onPress={onDeletePress}
              style={styles.deleteButton}
            />
          )}
          <Button
            title={t('Cancel')}
            onPress={onClose}
            style={styles.cancelButton}
          />
          <Button title={t('Save')} onPress={onSave} isPrimary />
        </ButtonToolbar>
      </View>
      <Form onSubmit={onSave} autoComplete="off" style={styles.flex}>
        <ScrollView style={styles.scrollView}>
          <View style={styles.formBody}>
            {/* Dummy fields to prevent Safari autofill. */}
            <DummyLogin />
            <FormField
              render={(id) => (
                <View style={styles.formField}>
                  <Text accessibilityRole="label" htmlFor={id}>
                    {t('Name')}
                  </Text>
                  <TextInputWithError
                    errorMessage={t('This field must have a valid name')}
                    id={id}
                    value={formData.name}
                    checkError={(value) => value.length > 1}
                    onChangeText={(value) => onChange('name', value)}
                  />
                </View>
              )}
            />
            <FormField
              render={(id) => (
                <View style={styles.formField}>
                  <Text accessibilityRole="label" htmlFor={id}>
                    {t('Email')}
                  </Text>
                  <TextInputWithError
                    keyboardType="email-address"
                    errorMessage={t('Must be a valid email address')}
                    id={id}
                    value={formData.email}
                    checkError={(value) => isValidEmail(value)}
                    onChangeText={(value) => onChange('email', value)}
                  />
                </View>
              )}
            />
            <FormField
              render={(id) => (
                <View style={styles.formField}>
                  <Text accessibilityRole="label" htmlFor={id}>
                    {t('Username')}
                  </Text>
                  <TextInputWithError
                    errorMessage={t(
                      'Please enter a valid username (min length 3)',
                    )}
                    id={id}
                    value={formData.username}
                    checkError={(value) => VALID_USERNAME.test(value)}
                    onChangeText={(value) => {
                      onChange('username', value.toLowerCase().trim());
                    }}
                  />
                </View>
              )}
            />
            <FormField
              render={(id) => (
                <View style={styles.formField}>
                  <Text accessibilityRole="label" htmlFor={id}>
                    {t('Password')}
                  </Text>
                  <TextInputPassword
                    id={id}
                    skipFirstCheck
                    value={formData.password}
                    checkPassword={(value) => onCheckPassword(value, user)}
                    placeholder={t('Leave blank to keep the old password')}
                    errorMessage={t(
                      'Minimum length 8; must include uppercase, lowercase and number.',
                    )}
                    onChangeText={(value) => onChange('password', value)}
                  />
                </View>
              )}
            />
            <BooleanInput
              label={t('Suspend User')}
              value={formData.suspended}
              onChange={(value) => onChange('suspended', value)}
              style={[styles.formField, { marginTop: 22 }]}
            />
            {userPermissions && (
              <>
                <View style={styles.subHeader}>
                  <Text style={styles.headingTwo}>{t('User Permissions')}</Text>
                </View>
                <View style={styles.permissions}>
                  {/* It will iterate through account permissions only once these are set */}
                  {userPermissions.map((permission: AccountPermissionKeys) => {
                    return (
                      <PermissionSelectRadio
                        key={`${permission}-key`}
                        permissionType={permission}
                        permissionAction={
                          formData.permissions?.[permission] ?? 'NONE'
                        }
                        onChange={(value) => {
                          onChangePermissions(
                            permission,
                            // TODO: Is there a way to get this type without setting as PermissionAction here?
                            // It is unknown at a lower level
                            value as AccessLevel,
                          );
                        }}
                      />
                    );
                  })}
                </View>
              </>
            )}
          </View>
        </ScrollView>
      </Form>
    </View>
  );
}

function onCheckPassword(value: string, user?: User | null) {
  if (user && value.length === 0) {
    return true;
  } else {
    return isPasswordStrengthEnough(value);
  }
}

function checkUserFormData(formData: FormData, user?: User | null) {
  let { name, email, username, password } = formData;
  let errorMessageArray = [];
  if (name.length < 1) {
    errorMessageArray.push(t('You require a name'));
  }
  if (!isValidEmail(email)) {
    errorMessageArray.push(t('You need a valid email'));
  }
  if (username.length < 1) {
    errorMessageArray.push(t('You require a username'));
  }
  if (!onCheckPassword(password, user)) {
    errorMessageArray.push(
      t('Minimum length 8; must include uppercase, lowercase and number.'),
    );
  }
  if (errorMessageArray.length > 0) {
    return { success: false, errorMessage: errorMessageArray.join('\n') };
  }
  return { success: true };
}

let styles = StyleSheet.create({
  root: {
    flexGrow: 1,
    backgroundColor: themeColors.white,
    borderRadius: 5,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.withOpacity.blue50,
  },
  header: {
    paddingVertical: 20,
    marginHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: themeColors.withOpacity.blue50,
  },
  subHeader: {
    paddingVertical: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: themeColors.withOpacity.blue50,
  },
  headerNewAccount: {
    paddingVertical: 6,
  },
  deleteButton: {
    borderColor: themeColors.red,
  },
  headingTwo: {
    fontSize: 16,
    lineHeight: 22,
    padding: 0,
    color: '#444',
    fontWeight: '600',
  },
  scrollView: {
    flex: 1,
    height: 0,
  },
  flex: {
    flex: 1,
  },
  formBody: {
    flex: 1,
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  formField: {
    marginVertical: 10,
  },
  cancelButton: {
    borderWidth: StyleSheet.hairlineWidth,
    backgroundColor: themeColors.backgroundGrey,
  },
  toolbar: {
    paddingVertical: 10,
    justifyContent: 'flex-end',
  },
  emptyState: {
    padding: 40,
  },
  emptyStateText: {
    textAlign: 'center',
  },
  permissions: { flexDirection: 'row', flexWrap: 'wrap', marginVertical: 20 },
});

export default UserDetailsContainer;
